import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';
import { HousingReportData } from '../../types/housingReport';
import {
  ArrowRight,
  CalendarDays,
  CircleAlert,
  Download,
  Loader2,
  MapPin,
  Printer,
  Share2,
  Search,
  TrendingUp,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
  EmailIcon,
} from 'react-share';
import { downloadHousingReportPdf, printHousingReport } from './utils';
import PseoEmailCapture from '../../components/pseo/PseoEmailCapture';
import { ensureDatasetDescriptions } from '../../lib/structuredData';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const HousingCountyReportPage: React.FC = () => {
  const { county } = useParams<{ county: string }>();
  const [report, setReport] = useState<HousingReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const countySlug = (county || '').toLowerCase();
  const countyName = useMemo(() => toTitleCase(countySlug || 'ireland'), [countySlug]);
  const browseLink = `/browse?location=${encodeURIComponent(countyName)}`;

  useEffect(() => {
    if (!countySlug) {
      setError('County is required.');
      setLoading(false);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(
          `${CLOUD_FUNCTIONS_BASE_URL}/getHousingReport?countyId=co-${countySlug}`
        );
        const result = await response.json();

        if (!result.success || !result.data) {
          setError(`No housing report found for ${countyName}.`);
          setReport(null);
          return;
        }

        setReport(result.data as HousingReportData);
      } catch (fetchError) {
        console.error(fetchError);
        setError(`Unable to load the housing report for ${countyName} right now.`);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [countyName, countySlug]);

  const maxMonthlyAverage = useMemo(() => {
    if (!report?.monthlyTrend.length) return 1;
    return Math.max(...report.monthlyTrend.map((item) => item.averagePrice), 1);
  }, [report]);
  const schema = useMemo(
    () => ensureDatasetDescriptions(report?.schema ?? null, report?.metaTags?.description ?? ''),
    [report?.metaTags?.description, report?.schema]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-teal-700 animate-spin" />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-xl w-full bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <CircleAlert className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Housing Report Unavailable</h1>
          <p className="text-slate-600 mb-6">{error || 'The report could not be loaded.'}</p>
          <Link
            to="/housing-reports"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600"
          >
            Back to Discover
          </Link>
        </div>
      </div>
    );
  }

  const canonicalUrl = `https://rentsmart.ie/housing-reports/${report.slug}`;
  const displayFaqs = [...report.faqs, ...(report.aiEnrichment?.localFaqs || [])];
  const shareTitle = `County ${report.countyName} Housing Report`;

  return (
    <>
      <Helmet>
        <title>{report.metaTags.title}</title>
        <meta name="description" content={report.metaTags.description} />
        <meta name="keywords" content={report.metaTags.keywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={report.metaTags.title} />
        <meta property="og:description" content={report.metaTags.description} />
        <meta property="og:url" content={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <style>{`
          @media print {
            @page {
              margin: 18mm;
            }

            body {
              background: #ffffff !important;
              color: #0f172a !important;
            }

            .print-hide {
              display: none !important;
            }

            .print-page {
              width: 100% !important;
              max-width: none !important;
              left: 0 !important;
              right: 0 !important;
              margin: 0 !important;
              background: #ffffff !important;
            }

            .print-hero {
              background: #ffffff !important;
              color: #0f172a !important;
              padding-top: 0 !important;
            }

            .print-hero p {
              color: #334155 !important;
            }

            .print-card {
              border-color: #e2e8f0 !important;
              background: #f8fafc !important;
              color: #0f172a !important;
            }

            .print-article,
            .print-aside {
              page-break-inside: avoid;
            }
          }
        `}</style>
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] bg-slate-50 pb-16 print-page">
        <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-cyan-900 to-teal-900 text-white pt-16 pb-20 px-4 print-hero">
          <div className="absolute -top-24 -left-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl print-hide" />
          <div className="absolute -bottom-20 -right-10 h-80 w-80 rounded-full bg-emerald-400/20 blur-3xl print-hide" />

          <div className="relative max-w-6xl mx-auto">
            {/* <div className="text-xs uppercase tracking-widest text-cyan-200 mb-4">{report.hero.eyebrow}</div> */}
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
              {report.hero.headline}
            </h1>
            <p className="max-w-3xl text-cyan-100 text-lg leading-relaxed mb-8">
              {report.hero.summary}
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 mb-8 print-hide">
              <span className="flex items-center gap-2 text-cyan-100 text-sm font-medium">
                <Share2 className="h-4 w-4" /> Share report:
              </span>
              <div className="flex gap-3">
                <FacebookShareButton url={canonicalUrl}>
                  <FacebookIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </FacebookShareButton>
                <TwitterShareButton url={canonicalUrl} title={shareTitle}>
                  <TwitterIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </TwitterShareButton>
                <WhatsappShareButton url={canonicalUrl} title={shareTitle}>
                  <WhatsappIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </WhatsappShareButton>
                <LinkedinShareButton url={canonicalUrl} title={shareTitle}>
                  <LinkedinIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </LinkedinShareButton>
                <EmailShareButton
                  url={canonicalUrl}
                  subject={`${shareTitle} | RentSmart.ie`}
                  body={`Check out the latest housing report for ${report.countyName}: `}
                >
                  <EmailIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </EmailShareButton>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4 print-card">
                <div className="text-cyan-100 text-xs uppercase mb-1">Median Price</div>
                <div className="text-xl font-bold">
                  {formatCurrency(report.summaryStats.medianPrice)}
                </div>
              </div>
              {/* <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4">
                <div className="text-cyan-100 text-xs uppercase mb-1">YoY Change</div>
                <div className="text-xl font-bold">{report.summaryStats.yoyChange.toFixed(1)}%</div>
              </div> */}
              <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4 print-card">
                <div className="text-cyan-100 text-xs uppercase mb-1">Sales Volume</div>
                <div className="text-xl font-bold">{report.summaryStats.salesVolume}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/10 backdrop-blur-sm p-4 print-card">
                <div className="text-cyan-100 text-xs uppercase mb-1">Last Updated</div>
                <div className="text-xl font-bold">{report.hero.lastUpdatedLabel}</div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 print-hide">
              <button
                type="button"
                onClick={() => downloadHousingReportPdf(countySlug)}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
              >
                <Download className="h-4 w-4" /> Download PDF
              </button>
              <button
                type="button"
                onClick={printHousingReport}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
              >
                <Printer className="h-4 w-4" /> Print Report
              </button>
              <Link
                to="/housing-reports"
                className="no-underline inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/20"
              >
                Discover Reports <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2 space-y-8 print-article">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">County Snapshot</h2>
              <p className="text-slate-700 leading-relaxed mb-5">{report.marketNarrative}</p>
              {report.aiEnrichment?.marketPulse && (
                <div className="mb-5 rounded-xl bg-cyan-50 border border-cyan-100 p-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    {report.aiEnrichment.marketPulse.headline}
                  </h3>
                  <p className="text-sm text-slate-700 mb-2">
                    {report.aiEnrichment.marketPulse.summary}
                  </p>
                  <div className="inline-flex text-xs font-semibold uppercase tracking-wide px-2 py-1 rounded bg-cyan-100 text-cyan-800">
                    Demand Meter: {report.aiEnrichment.marketPulse.demandMeter}
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                {report.highlights.map((highlight) => (
                  <div
                    key={highlight}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
                  >
                    {highlight}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-5">12-Month Trend</h2>
              <div className="space-y-3">
                {report.monthlyTrend.map((item) => (
                  <div key={item.month}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-slate-700">{item.label}</span>
                      <span className="text-slate-600">{formatCurrency(item.averagePrice)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-500"
                        style={{
                          width: `${Math.max((item.averagePrice / maxMonthlyAverage) * 100, 4)}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-5">Property Type Breakdown</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-3">Type</th>
                      <th className="py-3">Sales</th>
                      <th className="py-3">Median</th>
                      <th className="py-3">Average</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.propertyTypeBreakdown.map((item) => (
                      <tr
                        key={item.propertyType}
                        className="border-b border-slate-100 text-slate-700"
                      >
                        <td className="py-3 font-medium">{item.propertyType}</td>
                        <td className="py-3">{item.salesCount}</td>
                        <td className="py-3">{formatCurrency(item.medianPrice)}</td>
                        <td className="py-3">{formatCurrency(item.averagePrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-5">Recent Transactions</h2>
              <div className="space-y-3">
                {report.recentSales.slice(0, 12).map((sale, index) => (
                  <div
                    key={`${sale.address}-${sale.date}-${index}`}
                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200"
                  >
                    <div>
                      <div className="font-medium text-slate-900">{sale.address}</div>
                      <div className="text-xs text-slate-500">{sale.date}</div>
                    </div>
                    <div className="font-semibold text-teal-700">{formatCurrency(sale.price)}</div>
                  </div>
                ))}
              </div>
            </div>

            {report.aiEnrichment?.neighbourhoodSignals?.length ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-5">Neighbourhood Signals</h2>
                <div className="space-y-3">
                  {report.aiEnrichment.neighbourhoodSignals.map((item) => (
                    <div
                      key={`${item.area}-${item.signal}`}
                      className="rounded-lg border border-slate-200 p-4 bg-slate-50"
                    >
                      <div className="text-sm font-semibold text-slate-900">{item.area}</div>
                      <div className="text-sm text-slate-700 mt-1">{item.signal}</div>
                      <div className="text-xs text-slate-500 mt-2">{item.implication}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {report.aiEnrichment?.buyerPlaybook?.length ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-5">Buyer Playbook</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {report.aiEnrichment.buyerPlaybook.map((item) => (
                    <div
                      key={`${item.title}-${item.detail}`}
                      className="rounded-lg border border-slate-200 p-4 bg-slate-50"
                    >
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-700">{item.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {report.aiEnrichment?.sellerPlaybook?.length ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-5">Seller Playbook</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {report.aiEnrichment.sellerPlaybook.map((item) => (
                    <div
                      key={`${item.title}-${item.detail}`}
                      className="rounded-lg border border-slate-200 p-4 bg-slate-50"
                    >
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-slate-700">{item.detail}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <PseoEmailCapture
              pageType="housing-reports"
              pageSlug={report.slug}
              locationName={report.countyName}
              contextHeadline={`Get ${report.countyName} Housing Market Updates`}
              contextReason={`Receive Irish property updates relevant to ${report.countyName}, including local sales trend shifts, buyer/seller strategy updates, and county-level report refreshes.`}
            />

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-5">FAQs</h2>
              <div className="space-y-4">
                {displayFaqs.map((faq) => (
                  <div key={faq.question} className="border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-1">{faq.question}</h3>
                    <p className="text-sm text-slate-700">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <aside className="space-y-6 print-aside">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-teal-600" /> Active Local Areas
              </h2>
              <div className="space-y-3">
                {report.topLocalAreas.length ? (
                  report.topLocalAreas.map((item) => (
                    <div key={item.area} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{item.area}</span>
                      <span className="font-semibold text-slate-900">{item.listings}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    No area-level activity summary available.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-teal-600" /> Buyer Guidance
              </h2>
              <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
                {report.buyerGuidance.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h2 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-teal-600" /> Seller Guidance
              </h2>
              <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
                {report.sellerGuidance.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </div>

            {report.aiEnrichment?.renterOutlook ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-bold text-slate-900 mb-4">Renter Outlook</h2>
                <p className="text-sm text-slate-700 mb-3">
                  {report.aiEnrichment.renterOutlook.snapshot}
                </p>
                <div className="flex flex-wrap gap-2">
                  {report.aiEnrichment.renterOutlook.whoBenefitsMost.map((group) => (
                    <span
                      key={group}
                      className="px-2 py-1 rounded bg-slate-100 text-xs text-slate-700 border border-slate-200"
                    >
                      {group}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {report.aiEnrichment?.mortgageAndBudgetTips?.length ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h2 className="font-bold text-slate-900 mb-4">Mortgage & Budget Tips</h2>
                <ul className="space-y-2 text-sm text-slate-700 list-disc pl-5">
                  {report.aiEnrichment.mortgageAndBudgetTips.map((tip) => (
                    <li key={tip}>{tip}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-teal-50 p-6 print-hide">
              <h3 className="text-xl font-bold text-slate-900 mb-2">Need a home valuation next?</h3>
              <p className="text-sm text-slate-700 mb-4">
                Use county trends as context, then get a localised valuation estimate for your
                specific property.
              </p>
              <Link
                to={`/valuation/${countySlug}`}
                className="no-underline inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600"
              >
                Start Valuation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 print-hide">
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Search homes in {countyName}
              </h3>
              <p className="text-sm text-slate-700 mb-4">
                Jump straight into listings filtered for this county.
              </p>
              <Link
                to={browseLink}
                className="no-underline inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold hover:bg-slate-800"
              >
                Browse Listings <Search className="h-4 w-4" />
              </Link>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 print-hide">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Discover other reports</h3>
              <p className="text-sm text-slate-700 mb-4">
                Explore housing reports for every county in Ireland.
              </p>
              <Link
                to="/housing-reports"
                className="no-underline inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600"
              >
                Browse All Reports <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </section>
      </div>
    </>
  );
};

export default HousingCountyReportPage;
